import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { closetAPI, exportsAPI } from '../services/api';
import { formatFollowers, influencerPublicName } from '../utils/person';
import PersonAvatar from './PersonAvatar';
import Pagination from './Pagination';
import ExportCsvButton from './ExportCsvButton';
import './People.css';

const Closet = () => {
  const navigate = useNavigate();
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 15,
    total: 0,
  });
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  useEffect(() => {
    loadInfluencers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  const loadInfluencers = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: pagination.per_page,
      };
      if (search) params.search = search;
      if (activeFilter !== '') params.active_on_app = activeFilter;

      const response = await closetAPI.getInfluencers(params);
      setInfluencers(response.data.data || []);
      setPagination(response.data.pagination || pagination);
    } catch (error) {
      console.error('Error loading influencers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadInfluencers(1);
  };

  const handleAdd = () => {
    navigate('/closet/influencers/new');
  };

  const handleEdit = (influencer) => {
    navigate(`/closet/influencers/${influencer.id}/edit`, {
      state: { influencer },
    });
  };

  const handleViewProducts = (influencer) => {
    navigate(`/closet/influencers/${influencer.id}/products`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this closet account?')) return;
    try {
      await closetAPI.deleteInfluencer(id);
      loadInfluencers(pagination.page);
      toast.success('Closet account deleted');
    } catch (error) {
      toast.error(
        'Error deleting account: ' + (error.response?.data?.message || error.message)
      );
    }
  };

  if (loading && influencers.length === 0) {
    return <div className="loading">Loading closet accounts…</div>;
  }

  const totalLabel =
    pagination.total > 0
      ? `${pagination.total} closet account${pagination.total === 1 ? '' : 's'}`
      : 'Influencer closets on the platform';

  return (
    <div className="people-page">
      <div className="page-toolbar">
        <p className="page-meta">{loading ? 'Loading…' : totalLabel}</p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <ExportCsvButton
            exportRequest={exportsAPI.closet}
            filters={{
              search: search || undefined,
              active_on_app: activeFilter !== '' ? activeFilter : undefined,
            }}
            filename="closet.csv"
          />
          <button type="button" className="btn btn-primary" onClick={handleAdd}>
            + Add closet account
          </button>
        </div>
      </div>

      <div className="search-filter-bar">
        <div className="filter-field">
          <label htmlFor="closet-active">Active on app</label>
          <select
            id="closet-active"
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="1">Active</option>
            <option value="0">Not active</option>
          </select>
        </div>
        <div className="filter-field filter-field--grow">
          <label htmlFor="closet-search">Search</label>
          <input
            id="closet-search"
            type="search"
            placeholder="Name, email, full name, or social handle…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        <button type="button" className="btn btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>

      {loading && influencers.length > 0 ? (
        <div className="list-loading-hint">Updating…</div>
      ) : null}

      {influencers.length === 0 ? (
        <div className="people-empty">
          No closet accounts found.
          <div style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={handleAdd}>
              Add first account
            </button>
          </div>
        </div>
      ) : (
        <div className="people-list">
          {influencers.map((influencer) => {
            const name = influencerPublicName(influencer);
            const handle = influencer.instagram_handle
              ? `@${String(influencer.instagram_handle).replace(/^@/, '')}`
              : null;

            return (
              <article key={influencer.id} className="person-card">
                <PersonAvatar person={influencer} size="md" />

                <div className="person-card-main">
                  <h3 className="person-card-name">{name}</h3>
                  <p className="person-card-email">{influencer.email}</p>
                  <div className="person-card-meta">
                    <span className="role-pill closet">Closet</span>
                    {influencer.is_verified ? (
                      <span className="role-pill active">Active on app</span>
                    ) : (
                      <span className="role-pill pending">Not active on app</span>
                    )}
                    {influencer.username ? (
                      <span className="role-pill">@{String(influencer.username).replace(/^@/, '')}</span>
                    ) : null}
                    {handle ? <span className="role-pill">{handle}</span> : null}
                  </div>
                  <div className="person-card-stats">
                    <span>
                      <strong>{formatFollowers(influencer.follower_count)}</strong> followers
                    </span>
                    <span>
                      <strong>{influencer.closet_products_count ?? 0}</strong> products
                    </span>
                  </div>
                </div>

                <div className="person-card-actions">
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => handleViewProducts(influencer)}
                  >
                    Products
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => handleEdit(influencer)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(influencer.id)}
                  >
                    Delete
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {pagination.total > pagination.per_page && (
        <Pagination pagination={pagination} onPageChange={loadInfluencers} />
      )}
    </div>
  );
};

export default Closet;
